(ns ui.view.entity-graph.components.edge
  (:require [re-frame.core :as rf]
            [reagent.core :as r]
            [common.localization :as loc]
            [ui.state.session.sub :as session-sub]
            [ui.util :as uu]
            [ui.view.entity-graph.model :as m]
            [ui.view.entity-graph.utils :as utils]
            [ui.components.radix-ui.tooltip.view :as tooltip]
            [reagent-flow.core :as flow]))

(defn edge-will-be-hidden?
  [ctx id]
  @(r/track contains? (:hide-edges @(rf/subscribe [m/preview-show-hide ctx])) id))

(defn edge-will-appear?
  [ctx id]
  @(r/track contains? (:show-edges @(rf/subscribe [m/preview-show-hide ctx])) id))

(defn edge-label
  [{:keys [transform label will-be-hidden?]}]
  [:div
   {:class "bg-white text-base-light-text"
    :style
    (merge
     {:position "absolute"
      :border-radius "4px"
      :padding "4px"
      :font-size 11
      :font-weight 600
      :transform transform}
     (when will-be-hidden?
       {:color "transparent"
        :text-shadow "0 0 3px rgba(0,0,0,0.7)"}))}
   label])

;; for hot-reload:
#_(swap! re-frame.db/app-db
       (fn [db]
         (prn (get-in db [:ui.state.session.model/state :entities 1128703297161566]))
         (update-in db [:ui.state.session.model/state :entities 1128703297161566 :a]
                    (fnil inc 0))))

(defn custom-edge
  [{:keys [id _source _target data]
    source-x :sourceX source-y :sourceY
    target-x :targetX target-y :targetY
    source-handle-id :sourceHandleId
    target-handle-id :targetHandleId
    marker-end :markerEnd
    :as args}]
  (let [ctx (:ctx (js->clj data :keywordize-keys true))
        ctx {:main-entity-id (or (:main-entity-id ctx)
                                 (:mainEntityId ctx))}
        connector-id (parse-long id)
        connector (-> connector-id utils/get-entity)
        connector-type (:graph-reference/type connector)
        line-type @(rf/subscribe [m/connector-line-type
                                  (assoc ctx
                                         :connector-id
                                         connector-id
                                         :connector-type connector-type)])
        path-type @(rf/subscribe [m/connector-path-type
                                  (assoc ctx
                                         :connector-id
                                         connector-id
                                         :connector-type connector-type)])
        [path label-x label-y _offset-x _offset-y :as _path-defs]
        (case path-type
          :straight
          (#'flow/get-straight-path (clj->js args))

          :bezier
          (#'flow/get-bezier-path (clj->js args))

          :smooth-step
          (#'flow/get-smooth-step-path (clj->js args))

          (#'flow/get-smooth-step-path (clj->js args)))
        connection-label (case connector-type
                           :graph-reference.type/ownership
                           (when-let [percentage
                                      (utils/ownership-percentage connector)]
                             (utils/format-percentage percentage))
                           :graph-reference.type/lp "LP"
                           :graph-reference.type/gp "GP"
                           :graph-reference.type/management-company "ManCo"
                           :graph-reference.type/aifm "AIFM"
                           :graph-reference.type/advisor "Advisor"
                           nil)
        edge-params {:path path
                     :marker-end marker-end
                     :style
                     (merge
                      (when (= :dashed line-type)
                        {:stroke-dasharray "8 8"})
                      (when (edge-will-be-hidden? ctx connector-id)
                        {:stroke-dasharray "10,5"
                         :stroke-opacity "0.5"})
                      (when (edge-will-appear? ctx connector-id)
                        {:stroke-width "2"
                         :stroke "red"}))}
        info-transform (str "translate(-50%, -50%) "
                            "translate("
                            label-x
                            "px,"
                            label-y
                            "px)")

        label-position @(rf/subscribe [m/label-position
                                       (assoc ctx
                                              :connector-id connector-id)])
        [sx sy :as _start-label-translates] (case source-handle-id
                                              "bottom-source-handle" [-50 0]
                                              "top-source-handle" [-50 -100]
                                              "right-source-handle" [0 -50]
                                              "left-source-handle" [-100 -50])
        [ex ey :as _end-label-translates] (case target-handle-id
                                            "bottom-target-handle" [-50 0]
                                            "top-target-handle" [-50 -100]
                                            "right-target-handle" [0 -50]
                                            "left-target-handle" [-100 -50])
        ;; start-label-transform: label near source-handle
        ;; end-label-transform: label near target-handle
        start-label-transform (str "translate("
                                   sx "%, " sy "%) "
                                   "translate("
                                   source-x "px,"
                                   source-y "px)")
        end-label-transform (str "translate("
                                 ex "%, " ey "%) "
                                 "translate("
                                 target-x "px,"
                                 target-y "px)")]
    [:<>
     [flow/base-edge
      edge-params]
     (when connection-label
       [flow/edge-label-renderer
        [edge-label
         {:transform
          (case label-position
            :source start-label-transform
            :target end-label-transform
            :center info-transform
            info-transform)
          :will-be-hidden? (edge-will-be-hidden? ctx connector-id)
          :label connection-label}]])]))

(defn action-items
  [ctx connector]
  (let [editable? (utils/editable-graph-reference? ctx connector)
        connector-id (:db/id connector)]
    [(when editable?
       {:id (str "edit-" connector-id)
        :label (loc/tr :edit-connector)
        :on-select! (fn [_]
                      (rf/dispatch [m/open-edit-connector
                                    (assoc ctx
                                           :connector-id connector-id)])
                      (rf/dispatch [m/close-edge-menu ctx]))})
     {:id (str "hide-" connector-id)
      :label (loc/tr :hide)
      :on-select! (fn [_]
                    (rf/dispatch [m/hide-connector
                                  (assoc ctx
                                         :connector-id connector-id
                                         :hide? true)])
                    (rf/dispatch [m/close-edge-menu ctx]))}
     {:id (str "delete-" connector-id)
      :label (loc/tr :delete)
      :on-select! (fn [_]
                    (rf/dispatch [m/close-edge-menu ctx])
                    (rf/dispatch [m/start-delete
                                  (assoc ctx
                                         :connector-id connector-id)]))}]))

(defn action-view
  [_ctx {:keys [on-select! label]}]
  [:div
   {:class (uu/cm "p-2"
                  "cursor-pointer hover:bg-base-regular rounded")
    :on-click on-select!}
   label])

(defn context-menu
  [ctx]
  (let [{:keys [top left open? connector-id]} @(rf/subscribe [m/edge-menu ctx])]
    (when open?
      (let [connector @(rf/subscribe [::session-sub/entity connector-id])
            connector-type (:graph-reference/type connector)
            source-id (:graph-reference/source connector)
            target-id (:graph-reference/target connector)
            source @(rf/subscribe [::session-sub/entity source-id])
            target @(rf/subscribe [::session-sub/entity target-id])
            tooltip-text (loc/tr :edge-menu-info [(loc/tr connector-type)
                                              (:graph-entity/label source)
                                              (:graph-entity/label target)])
            actions (action-items ctx connector)]
        [:div
         {:id "edge-context-menu"
          :class (uu/cm "flex flex-col gap-1 bg-base-light min-w-36 border"
                        "border-base-light-border-paler border-solid p-3 absolute rounded")
          :style
          {:top top
           :left left
           :z-index 30}
          :on-key-down (fn [e]
                         (let [key-code (.-keyCode e)]
                           (when (= key-code 27)
                             (rf/dispatch [m/close-edge-menu ctx]))))
          :tab-index 0
          :ref (fn [^js ref]
                 (when ref
                   (.focus ref)))}
         [:div
          {:class "flex justify-between"}
          [tooltip/view
           {:delay-duration 100
            :side :top
            :align :center}
           [:span
            {:class (uu/cm "right-0 top-0 py-1 px-2 ml-2"
                           "badge badge-pill"
                           "bg-secondary-regular text-secondary-regular-text-paler opacity-75")}
            [:i
             {:class (uu/cm "fas fa-info")}]]
           tooltip-text]]
         (doall
          (for [{:keys [id] :as action} actions
                :when (some? action)]
            ^{:key (str id)}
            [action-view ctx action]))]))))
